import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbModal, NgbModalModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BehaviorSubject, Observable, switchMap, tap, timer } from 'rxjs';
import { DashboardData, Metadata } from '../models/data-result.model';
import { ArrayReverseFilterPipe } from '../pipes/array-reverse-filer.pipe';
import { FormatterPipe } from '../pipes/formatter.pipe';
import { ReplacePlaceholderWithUrlPipe } from '../pipes/replace-placeholder-with-url.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { OfferService } from '../services/offer.service';
import { HasRoleDirective } from '../shared/directives/has-role.directive';
import { LoadingComponent } from '../shared/spinner/loading/loading.component';
import { TemplateInputModalComponent } from '../template-input-modal/template-input-modal.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgbModalModule,
    NgbTooltipModule,
    ArrayReverseFilterPipe,
    FormatterPipe,
    ReplacePlaceholderWithUrlPipe,
    SafeHtmlPipe,
    HasRoleDirective,
    LoadingComponent,
  ]
})
export class DashboardComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);
  private readonly offerService = inject(OfferService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  showLoading: boolean = false;
  metadataSubject = new BehaviorSubject<string>(this.activatedRoute.snapshot.queryParams['quoteId']);
  metadata$: Observable<Metadata> = this.metadataSubject.asObservable().pipe(
    switchMap((quoteId) => this.offerService.getQuoteMetadata(quoteId)),
    tap((metadata) => {
      if (metadata?.templateDocumentId && !metadata.processedDocumentId) {
        timer(5000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.metadataSubject.next(metadata.quoteId));
      }
    })
  );



  dashboardData$: Observable<DashboardData> = this.offerService.dashboardData$(this.activatedRoute.snapshot.queryParams['quoteId']);

  ngOnInit(): void {
    // when I arrive from the web app I have to open the modal with the data of the templateDriveId
    if (this.activatedRoute.snapshot.queryParams['templateDriveId']) {
      this.showLoading = true;
      this.offerService
        .getDriveIdData(this.activatedRoute.snapshot.queryParams['templateDriveId'])
        .pipe(tap((_) => (this.showLoading = false)), takeUntilDestroyed(this.destroyRef))
        .subscribe((metadata: Metadata) => this.openEditModal(metadata));
    }
  }

  openEditModal(metadata: Metadata) {
    const modalRef = this.modalService.open(TemplateInputModalComponent, {
      size: 'xl',
      scrollable: true,
      backdrop: 'static', // da capire se si vuole o meno la possibilità di chiudere cliccando fuori
    });
    modalRef.componentInstance.dataTemplate = metadata;
    modalRef.componentInstance.quoteId = metadata.quoteId;
    modalRef.componentInstance.formData = this.offerService.resultForm;
    modalRef.componentInstance.inputForm = this.offerService.inputForm;
    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((quoteId) => {
      if (quoteId) {
        this.changeQueryParam({ quoteId });
        this.metadataSubject.next(quoteId);
      }
    });
  }

  changeQueryParam(params: Record<string, any>) {
    this.router.navigate(['.'], {
      relativeTo: this.activatedRoute,
      queryParams: { ...params },
    });
  }

  print(metadata: Metadata) {
    if (metadata.templateDocumentId) {
      this.showLoading = true;
      this.offerService.getPdf(metadata.processedDocumentId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
        saveAs(data, metadata.templateDriveName);
        this.showLoading = false;
      });
    } else {
      let data = document.getElementById('printSection');
      html2canvas(data, {
        ignoreElements: (element) => {
          return element.hasAttribute('no-print') || element.getAttribute('role') === 'tooltip';
        },
      }).then((canvas) => {
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const contentDataURL = canvas.toDataURL('image/png');
        let pdf = new jsPDF('p', 'mm', 'a4'); // A4 size page of PDF
        const position = 0;
        pdf.setFillColor('#f8f8f8');
        pdf.rect(0, 0, imgWidth, 500, 'F');

        pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);
        pdf.save(`${metadata.templateDriveName}.pdf`); // Generated PDF
      });
    }

  }

  copyQuoteUrl(metadata: Metadata) {
    navigator.clipboard.writeText(`${globalThis.location.origin}/dashboard?quoteId=${metadata.quoteId}`);
  }
  createOpportunity(metadata: Metadata) {
    this.router.navigate(['/opportunity'], {
      queryParams: {
        URL_Preventivatore__c: `${globalThis.location.origin}/dashboard?quoteId=${metadata.quoteId}`,
        Tipologia_Opportunit__c: 'Multi Cloud',
        Vendor__c: metadata.typology,
        Nome_servizio_a_catalogo__c: metadata.templateDriveName,
      },
    });
  }

  maxLength(section) {
    return section.reduce((acc, { value }) => Math.max(acc, value?.length || 0), 0);
  }

  sumDays(startDate, dateToAdd) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + Number.parseInt(dateToAdd));
    return date;
  }

}
